import React, { useState, useEffect, useMemo, useContext } from 'react';
import {formatDate, formatDateIntl} from '../../DateUtil'
import { LegacyStack, Link} from '@shopify/polaris';
import AddExtension from './AddExtension'
import AddOfficeHour from './AddOfficeHour'
import UpgradeFreeTrial from './UpgradeFreeTrial'
import UpgradeTempAccess from './UpgradeTempAccess'
import EditTransfer from './EditTransfer'
import EditAssigned from './EditAssigned'
import EditRetake from './EditRetake'
import EditRegisterRelated from './EditRegisterRelated'
import RecurInfo from './RecurInfo'
import QuickViewModal from './QuickViewModal'
import LoginAsParentModal from './LoginAsParentModal'
import OfficeHourable from '../../concerns/OfficeHourable'
import Productable from '../../concerns/Productable'
import Subscriptionable from '../../concerns/Subscriptionable'
import Extendable from '../../concerns/Extendable'
import Reassignable from '../../concerns/Reassignable'
import Retakeable from '../../concerns/Retakeable'
import Transferable from '../../concerns/Transferable'
import RelatedRegisterable from '../../concerns/RelatedRegisterable'
import { CustomerContext } from '../../contexts/Customer'
import { ShopContext } from '../../contexts/Shop'
import { OfficeHoursContext } from '../../contexts/OfficeHours'
import { OfficeHourSubscriptionsContext } from '../../contexts/OfficeHourSubscriptions'
import { HostContext } from '../../contexts/Host'
import { ProductsContext } from '../../contexts/Products'
import { LineItemsExtensionContext } from '../../contexts/LineItemsExtension'
import { SubscriptionCodesContext } from '../../contexts/SubscriptionCodes'
import { SubscriptionsContext } from '../../contexts/Subscriptions'
import { QuickViewsContext } from '../../contexts/QuickViews'
import { UserContext } from '../../contexts/yolk/User'
import { RecursContext } from '../../contexts/Recurs'
import FlippingBookModal from './FlippingBookModal'
import flippingBookData from './FlippingBookData'

export default function SubscriptionRowCanvas(props) {
  const [customer] = useContext(CustomerContext)
  const [user] = useContext(UserContext)
  const shop_name = useContext(ShopContext)
  const subscriptionCodes = useContext(SubscriptionCodesContext)
  const subscriptions = useContext(SubscriptionsContext)
  const officeHourSubscriptions = useContext(OfficeHourSubscriptionsContext)
  const [officeHours] = useContext(OfficeHoursContext)
  const lineItemsExtension = useContext(LineItemsExtensionContext)
  const [products] = useContext(ProductsContext)
  const host = useContext(HostContext);
  const [quickViews] = useContext(QuickViewsContext)
  const [recurs] = useContext(RecursContext)

  const subscription = subscriptions.find((s) => s.id == props.id)
  if(!subscription) {
    throw Error(`no subscription found for id ${props.id} in : ${subscriptions.map((s) => s.id)}`)
  }

  const [saving, setSaving] = useState(false);
  const officeHourableFlags = OfficeHourable.flags(officeHourSubscriptions, subscription, officeHours, shop_name)
  const imageSrc = Productable.imageSrc(products, subscription)
  const title = subscription.title(products, subscriptionCodes)
  const assignedToText = Subscriptionable.assignedToText(subscription, subscription.assignedToDisplay)
  const monthlySubscription = Subscriptionable.monthlySubscription(subscription, lineItemsExtension)
  const recur = monthlySubscription && recurs.find((recur) => recur.matches(monthlySubscription.orderId))
  const endAtText = Subscriptionable.endAtText(subscription, lineItemsExtension, {
    formatDate: formatDateIntl,
    prefix: 'Course ends: ',
    subscriptionText: 'Monthly subscription'
  })
  const allowLoginAsStudentFlags = Subscriptionable.allowLoginAsStudentFlags(subscription, user)
  const allowLoginAsParentFlags = Subscriptionable.allowLoginAsParentFlags(subscription, user)
  const extendableFlags = Extendable.flags(lineItemsExtension, subscription)
  const reassignableFlags = Reassignable.flags(subscription, subscriptionCodes, user)
  const retakeableFlags = Retakeable.flags(subscription, user)
  const transferableFlags = Transferable.flags(subscription, user)
  const relatedRegisterableFlags = RelatedRegisterable.flags(subscription, user)
  const goToCourseUrl = Subscriptionable.goToCourseUrl(subscription, lineItemsExtension, host, customer, shop_name)
  const goToCourse = subscription.error || (goToCourseUrl ?
    <span><a className="goto-course-link heading" href={goToCourseUrl}>{subscription.status == 'delayed_start' ? 'Start' : 'Go To Course'}</a> &rarr;</span>
     : Subscriptionable.goToCourseText(subscription, lineItemsExtension, subscriptionCodes))

  const quickViewActivity = quickViews.find((quickView) => quickView.matches(subscription, user, 'Activity'))
  const quickViewGrades = quickViews.find((quickView) => quickView.matches(subscription, user, 'Grades'))
  const isStudent = subscription.isStudent(user)
  const isActive = subscription.enrollment && subscription.enrollment.isActive
  const productBookData = subscription.sku ? flippingBookData[subscription.sku] : null

  return (<tr className={`subscription-row subscription-row-canvas ${saving ? 'subscription-row-saving' : ''}`}>
      <td className="thumbnail"><img src={imageSrc}/></td>
      <td colSpan="8">
        <table className="responsive subscriptions inner">
          <tbody>
          <tr>
            <td>
              <span className="heading name">{title}</span>
              <div className="subheading assigned-to">{assignedToText}</div>
              <div className="subsubheading">
              <LegacyStack>
              { !allowLoginAsStudentFlags.length ?
                <LegacyStack.Item><Link onClick={() => window.location.assign(`${host}/accounts/enrollments/login?enrollment_id=${subscription.enrollmentId}&shop_name=${shop_name}`)}>Login As Student</Link></LegacyStack.Item> : null
              }
              { !allowLoginAsParentFlags.length ?
                <LegacyStack.Item><LoginAsParentModal enrollment={subscription.enrollment} /></LegacyStack.Item> : null
              }
              { subscription.isUnassigned(subscriptionCodes) ?
                <LegacyStack.Item><span className="assign edit-assigned"><EditAssigned tabSelected="new" className="unassigned" subscription_id={subscription.id} onSaving={setSaving} unassignedLinkText="Assign and Start" /><span> &rarr;</span></span></LegacyStack.Item> :
                  !reassignableFlags.length ?
                    <LegacyStack.Item><span className="reassign edit-assigned"><EditAssigned tabSelected="new" subscription_id={subscription.id} onSaving={setSaving}/></span></LegacyStack.Item> :
                    <div className={`reassign ${reassignableFlags}`}></div>
              }
              </LegacyStack>
              </div>
            </td>
            { goToCourse ? <td className={`${subscription.error ? "error" : "goto-course"} actions`}>{ goToCourse }</td> : null }
            {
              !subscription.isUnassigned(subscriptionCodes) && subscription.isFromCanvas ?
              <td className="activity actions">
                <div className="heading">Activity</div>
                { subscription.lastActivityAt ? <div className="subheading">{formatDateIntl(subscription.lastActivityAt, true)}</div> : <div>--</div>}
                { quickViewActivity ? <div className="subheading"><QuickViewModal title="Activity Report" linkTitle="View Report" enrollmentId={subscription.enrollmentId} quickView={quickViewActivity}></QuickViewModal></div> : <div className="no_activity_quick_view"></div>}
              </td> : <td className="actions"></td>
            }
            {
              !subscription.isUnassigned(subscriptionCodes) && subscription.isFromCanvas ?
              <td className="grades actions">
                <div className="heading">Grades</div>
                { subscription.overallGrade ? <div className="subheading">{`Overall grade: ${subscription.overallGrade}%`}</div> : <div>--</div> }
                { quickViewGrades ? <div className="subheading"><QuickViewModal
                    title="Grade Report"
                    linkTitle={isStudent ? "View Grades" : "View Grades / Reset Tests"}
                    enrollmentId={subscription.enrollmentId}
                    quickView={quickViewGrades}>
                  </QuickViewModal></div> : <div className="no_grades_quick_view"></div>}
              </td> : <td className="actions"></td>
            }
          </tr>
          { isActive && !subscription.isUnassigned(subscriptionCodes) && productBookData ?
              <tr>
                <td colSpan="4" className="flip-book-links">
                  <LegacyStack>
                    <LegacyStack.Item><div className="flip-book-icon"></div></LegacyStack.Item>
                    <LegacyStack.Item style={{marginLeft: '8px'}}><strong>Course Companion Book</strong></LegacyStack.Item>
                    { Object.keys(productBookData).map((key) =>
                      <LegacyStack.Item key={key}><FlippingBookModal linkTitle={'Volume ' + key} data={productBookData[key]}/></LegacyStack.Item>
                    )}
                  </LegacyStack>
                </td>
              </tr> : null
          }
          <tr>
            <td colSpan="4">
            <div className="subsubheading">
            <LegacyStack>
              { subscription.isFreeTrial ? <LegacyStack.Item><span className="free-trial">Free Trial</span></LegacyStack.Item> : null }
              <LegacyStack.Item><span className="end-at">{monthlySubscription ?
                <RecurInfo
                  subscription={subscription}
                  recur={recur}
                /> : endAtText }
              </span></LegacyStack.Item>
              { !subscription.isUnassigned(subscriptionCodes) && subscription.isFreeTrial ?
                <LegacyStack.Item><UpgradeFreeTrial subscription_id={subscription.id} submitClassName="Polaris-Link" /></LegacyStack.Item> : null
              }
              { !extendableFlags.length ?
                <LegacyStack.Item><AddExtension enrollment_id={subscription.enrollmentId} course_name={title} /></LegacyStack.Item> : <LegacyStack.Item><div className={extendableFlags}></div></LegacyStack.Item>
              }
              { !officeHourableFlags.length ?
                <LegacyStack.Item><AddOfficeHour subscription_id={subscription.id} /></LegacyStack.Item> : <div className={officeHourableFlags}></div>
              }
            </LegacyStack>
            </div>
            </td>
          </tr>
          </tbody>
        </table>
      </td>
    </tr>)
}
